import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingRoadmapHelmet from 'common/helmets/LandingRoadmapHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import FeaturesChildPagePagination from 'common/www/FeaturesChildPagePagination';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import { Quote, QuotePerson, Quotes } from 'common/www/Quotes';
import Aurelien from 'img/landing/aurelien.webp';
import Jesse from 'img/landing/jesse.webp';
import Point2 from 'img/landing/public-roadmap-1.webp';
import Point1_1x from 'img/landing/roadmap-1-1x.webp';
import Point1_2x from 'img/landing/roadmap-1-2x.webp';
import Point3 from 'img/landing/roadmap-3.webp';
import Saravana from 'img/landing/saravana-small.webp';

import 'css/components/www/_LandingFeaturesChildPage.scss';

class LandingRoadmapPage extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Roadmap Child Page');
  }

  render() {
    return (
      <div className="landingRoadmapPage landingFeaturesChildPage">
        <LandingRoadmapHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="text">
              <h1>
                Product roadmap tools to help you prioritize features and build your&nbsp;roadmap
              </h1>
              <p className="subheading">
                Score features, build your product roadmap, and keep stakeholders in the&nbsp;loop.
              </p>
              <LandingCTAButtons />
            </div>
          </div>
        </div>
        <div className="mainContentContainer">
          <h2>Determine what to build next and keep everyone&nbsp;updated</h2>
          <div className="point">
            <LazyLoadedImage
              loading="eager"
              src={Point1_1x}
              srcSet={`${Point1_1x} 1x, ${Point1_2x} 2x`}
              alt="Canny's product roadmap software with prioritization scoring"
            />
            <div className="text">
              <h2 className="title">Prioritize projects and&nbsp;features</h2>
              <p>
                Rank and score projects and feature requests so you know you’re working on the most
                impactful&nbsp;things.
              </p>
              <ul>
                <li>
                  Customize your prioritization scoring based on the factors that matter to your
                  team and your product&nbsp;goals
                </li>
                <li>
                  Integrate customer feedback and feature request votes into your
                  prioritization&nbsp;process
                </li>
                <li>
                  Build roadmaps based on which product feature request scores the&nbsp;highest
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point2}
              alt="Canny's roadmap tool showing features under review, planned, and in progress"
            />
            <div className="text">
              <h2 className="title">Communicate progress to customers and key stakeholders</h2>
              <p>
                Don’t let feedback collection feel like a black hole. Communicate product strategy
                and decisions to keep users and stakeholders updated with public or
                private&nbsp;roadmaps.
              </p>
              <ul>
                <li>
                  Notify customers and external stakeholders about new features to
                  drive&nbsp;engagement
                </li>
                <li>
                  Track project status and keep your internal stakeholders on the same&nbsp;page
                </li>
                <li>
                  Show that your team is working hard to improve your product — let users
                  track&nbsp;progress!
                </li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage
              src={Point3}
              alt="Canny's tool to automate product announcements to stakeholders"
            />
            <div className="text">
              <h2 className="title">Sync with project management&nbsp;tools</h2>
              <p>Sync statuses between platforms while maintaining a cleaner&nbsp;backlog.</p>
              <ul>
                <li>
                  Push priorities to project management tools like GitHub and Jira without
                  disrupting your existing&nbsp;workflow
                </li>
                <li>
                  See a real-time view of project statuses (instead of manually updating
                  a&nbsp;spreadsheet) in a project&nbsp;roadmap
                </li>
                <li>
                  Keep your product backlog in Canny, instead of cluttering your project
                  management&nbsp;tools
                </li>
              </ul>
            </div>
          </div>
          <FeaturesChildPagePagination currentPage="roadmap" />
          <Quotes title={<>Leading companies rely on Canny's product roadmap&nbsp;software</>}>
            <Quote
              header={<>GiveButter uses Canny to build feedback-driven product&nbsp;roadmaps</>}
              text={
                <>
                  We want to let customer feedback really drive our roadmap. Not only on small
                  enhancements and changes, but also on our major&nbsp;features.
                </>
              }
              person={
                <QuotePerson
                  name="Jesse Sandala"
                  image={Jesse}
                  title={<>Director of Product,&nbsp;GiveButter</>}
                />
              }
            />
            <Quote
              header={<>Document360 builds trust with their public&nbsp;roadmap</>}
              text={
                <>
                  Our Canny roadmap is public, so the whole world can see what we’re building. We
                  believe that it adds credibility and instills&nbsp;trust.
                </>
              }
              person={
                <QuotePerson
                  name="Saravana Kumar"
                  image={Saravana}
                  title={<>Founder,&nbsp;Document360</>}
                />
              }
            />
            <Quote
              header={<>Strapi built a community around their product&nbsp;development</>}
              text={
                <>
                  We wanted to have a public roadmap where the community could interact with us and
                  each other. It was important for us to give them a place where they can&nbsp;chat.
                </>
              }
              person={
                <QuotePerson
                  name="Aurélien Georget"
                  image={Aurelien}
                  title={<>Chief Product Officer,&nbsp;Strapi</>}
                />
              }
            />
          </Quotes>
          <FAQs
            faqs={[
              {
                question: 'Can I build roadmaps for multiple products and features?',
                answer:
                  '<p>Definitely! Your product team can build as many roadmaps as you need, and create as many feedback boards as you need. You can also associate each product owner or product manager with specific boards and roadmaps. This way, they only see activity on the boards and roadmaps that matter to them.</p>',
                html: (
                  <p>
                    Definitely! Your product team can build as many roadmaps as you need, and create
                    as many feedback boards as you need. You can also associate each product owner
                    or product manager with specific boards and roadmaps. This way, they only see
                    activity on the boards and roadmaps that matter to them.
                  </p>
                ),
              },
              {
                question: 'Is Canny just a product roadmap tool?',
                answer: `<div><p>Nope! While our roadmapping tool is a big feature for us, we offer a full suite of product management software. We can help you:</p><ul><li>Collect user feedback</li><li>Manage feedback</li><li>Analyze feedback</li><li>Prioritize new features</li><li>Build your public roadmap</li><li>Manage your changelog</li></ul><p>You're covered for the entire feedback cycle!</p></div>`,
                html: (
                  <>
                    <p>
                      Nope! While our roadmapping tool is a big feature for us, we offer a full
                      suite of product management software. We can help you:
                    </p>
                    <ul className="orderedList">
                      <li className="orderedListItem">Collect user feedback</li>
                      <li className="orderedListItem">Manage feedback</li>
                      <li className="orderedListItem">Analyze feedback</li>
                      <li className="orderedListItem">Prioritize new features</li>
                      <li className="orderedListItem">Build your public roadmap</li>
                      <li className="orderedListItem">Manage your changelog</li>
                    </ul>
                    <p>You're covered for the entire feedback cycle!</p>
                  </>
                ),
              },
              {
                question: 'How does the roadmap planner work?',
                answer: `<p>First, you need to set up your roadmap. Then, you can add each new feature you plan to build to the roadmap. Canny also lets you add any ideas from your feedback board to a visual roadmap you build. This lets your stakeholders see what's being worked on next and understand your product plan. For more detailed info, check out our <a href="https://help.canny.io/en/articles/3828148-end-user-roadmap" target="_blank" rel="noopener">help doc</a>.</p>`,
                html: (
                  <p>
                    First, you need to set up your roadmap. Then, you can add each new feature you
                    plan to build to the roadmap. Canny also lets you add any ideas from your
                    feedback board to a visual roadmap you build. This lets your stakeholders see
                    what's being worked on next and understand your product plan. For more detailed
                    info, check out our{' '}
                    <a
                      href="https://help.canny.io/en/articles/3828148-end-user-roadmap"
                      target="_blank"
                      rel="noopener">
                      help&nbsp;doc
                    </a>
                    .
                  </p>
                ),
              },
              {
                question: 'Can I maintain an internal roadmap?',
                answer:
                  '<p>Definitely! Our roadmapping software is connected to your feedback board. Just set that to private and restrict access to your team. This lets you foster team collaboration in roadmap planning away from public eyes.</p>',
                html: (
                  <p>
                    Definitely! Our roadmapping software is connected to your feedback board. Just
                    set that to private and restrict access to your team. This lets you foster team
                    collaboration in roadmap planning away from public eyes.
                  </p>
                ),
              },
              {
                question: 'How much does it cost?',
                answer:
                  "<p>We have plans to suit most companies' requirements and budgets. You can get started with our Free plan. It includes completely free product roadmap tools. You can use them until you need more powerful features and integrations (actually!). Then, you can look at our Startup, Growth, or Business plans.</p>",
                html: (
                  <p>
                    We have plans to suit most companies' requirements and budgets. You can get
                    started with our Free plan. It includes completely free product roadmap tools.
                    You can use them until you need more powerful features and integrations
                    (actually!). Then, you can look at our Startup, Growth, or Business plans.
                  </p>
                ),
              },
              {
                question: 'How many team members can use Canny?',
                answer:
                  '<p>As many as you want! All Canny plans include unlimited contributors, so add everyone who can help you reach your product goals. While product teams are usually the most involved, adding other team members is common (and very useful!). You can add customer success, design, sales, software development, and project management team members to help you build your next big feature!</p>',
                html: (
                  <p>
                    As many as you want! All Canny plans include unlimited contributors, so add
                    everyone who can help you reach your product goals. While product teams are
                    usually the most involved, adding other team members is common (and very
                    useful!). You can add customer success, design, sales, software development, and
                    project management team members to help you build your next big feature!
                  </p>
                ),
              },
            ]}
          />
          <LandingFooterCTA style="solid" expanded={true} />
        </div>
      </div>
    );
  }
}

export default compose(withContexts({ trackEvent: TrackEventContext }), hot)(LandingRoadmapPage);
